import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  Bell,
  Briefcase,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Contact,
  CreditCard,
  FileSignature,
  FileText,
  History,
  Home,
  Landmark,
  LifeBuoy,
  Package,
  Receipt,
  Settings,
  Truck,
  UserCog,
  Users,
  Wallet,
} from 'lucide-react';

import type { AccessRule } from '../auth/useAccess.js';

/**
 * THE one nav list. Both shells render from this file and from nothing else:
 * the sidebar shell reads `NAV_GROUPS` + `HOME_ITEM`; the platform shell reads
 * `LAUNCHER_GROUPS` + `BAR_TABS` + `CHROME_DESTINATIONS`. Destinations are
 * declared exactly once in `NAV_DESTINATIONS` — path, icon, label key, access
 * rule — and every grouping references them, so a destination can never be
 * reachable in one shell and missing (or differently guarded) in the other.
 *
 * Every destination carries an `access` rule — `scripts/check-nav-permissions.mjs`
 * fails the build if one is missing, which is the only thing keeping this map
 * from drifting again. Each rule MIRRORS the guard the api worker puts on that
 * destination's own list endpoint; if you change one side, change the other.
 * `'always'` is a legitimate answer but has to be written down, never left off.
 */
export interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  access: AccessRule;
  /**
   * How this destination presents itself in the cmd-K palette. REQUIRED: every destination is
   * reachable from the keyboard, so the palette and the sidebar can never disagree about which
   * places exist — the platform shell's own SearchPanel already offers all of them, and an
   * optional field here is how the two would quietly drift apart again.
   *
   * The palette used to be a SECOND, hand-written list of ten entries sitting beside these
   * destinations with nothing tying the two together, so a destination could be gated one way in
   * the sidebar and another way (or not at all) in the palette. Declaring the palette here means
   * the entry cannot carry a different access rule from the nav item it jumps to — there is only
   * one rule, on this object.
   */
  palette: PaletteEntry;
}

/** How a destination presents itself in the cmd-K palette. */
export interface PaletteEntry {
  /**
   * Extra words the palette matches on, beyond the translated label and description. These are
   * deliberately raw English rather than translation keys: they are search hooks for what a user
   * TYPES ("schedule" for Dispatch, "quotes" for Estimates), not copy anyone reads.
   */
  keywords?: string[];
  /**
   * The one-line `nav:search.*` key shown under the label. REQUIRED: a palette where half the
   * rows carry a subtitle and half do not reads as a rendering fault rather than a copy gap, so
   * a destination cannot join the palette without one. `AppShell.test.tsx` checks the key
   * actually resolves in BOTH locale files, which the type cannot.
   */
  descriptionKey: string;
}

export interface NavGroup {
  labelKey: string;
  icon: LucideIcon;
  items: NavItem[];
}

/** A launcher group header with the destinations filed under it, in order. */
export interface LauncherGroup {
  labelKey: string;
  items: NavItem[];
}

// The rules below were read off the api worker's own route guards (165 routes
// scanned), NOT guessed from the label. Two of them are role-based rather than
// key-based because the server guards them that way:
//   /estimates + /employees -> requireManagerOrAdmin
//   /payroll                -> requireApiAdmin  (stricter than Manager+)
const NAV_DESTINATIONS = {
  home: {
    to: '/',
    labelKey: 'nav:items.dashboard',
    icon: Home,
    // The Home KPI dashboard is the index route, so gating it would leave a
    // low-permission user with nowhere to land. Note the KPI data behind it
    // (`GET /api/v1/overview`) DOES require `reports.view`, so an Employee
    // reaching Home gets an empty dashboard rather than a missing one.
    access: 'always',
    palette: { keywords: ['home', 'overview'], descriptionKey: 'nav:search.dashboard' },
  },
  jobs: {
    to: '/jobs',
    labelKey: 'nav:items.jobs',
    icon: Briefcase,
    access: { permission: 'jobs.view' },
    palette: { keywords: ['work orders', 'projects'], descriptionKey: 'nav:search.jobs' },
  },
  dispatch: {
    to: '/dispatch',
    labelKey: 'nav:items.dispatch',
    icon: CalendarDays,
    access: { permission: 'jobs.view' },
    palette: {
      keywords: ['schedule', 'calendar', 'assignments'],
      descriptionKey: 'nav:search.dispatch',
    },
  },
  servicetitan: {
    to: '/servicetitan',
    labelKey: 'nav:items.servicetitan',
    icon: CalendarDays,
    // Manager+ mirrors requireManagerOrAdmin on GET /servicetitan/schedule. A ROLE rule, not a
    // permission key, because the api worker guards that route by role — and Manager, not
    // Admin, or the server would allow a Manager the page while the nav never showed it.
    // Whether the destination EXISTS for a tenant is a second question, answered by
    // useFeatureAccess (`servicetitan` flag) at render time, not here.
    access: { minRole: 'Manager' },
    palette: {
      keywords: ['integration', 'schedule'],
      descriptionKey: 'nav:search.servicetitan',
    },
  },
  inventory: {
    to: '/inventory',
    labelKey: 'nav:items.inventory',
    icon: Package,
    access: { permission: 'inventory.view' },
    palette: {
      keywords: ['stock', 'parts', 'materials'],
      descriptionKey: 'nav:search.inventory',
    },
  },
  fleet: {
    to: '/fleet',
    labelKey: 'nav:items.fleet',
    icon: Truck,
    access: { permission: 'fleet.view' },
    palette: { keywords: ['vehicles', 'trucks'], descriptionKey: 'nav:search.fleet' },
  },
  estimates: {
    to: '/estimates',
    labelKey: 'nav:items.estimates',
    icon: ClipboardList,
    access: { minRole: 'Manager' },
    palette: {
      keywords: ['quotes', 'proposals', 'bids'],
      descriptionKey: 'nav:search.estimates',
    },
  },
  invoices: {
    to: '/invoices',
    labelKey: 'nav:items.invoices',
    icon: FileText,
    access: { permission: 'invoices.view' },
    palette: {
      keywords: ['billing', 'payments'],
      descriptionKey: 'nav:search.invoices',
    },
  },
  serviceAgreements: {
    to: '/service-agreements',
    labelKey: 'nav:items.serviceAgreements',
    icon: FileSignature,
    access: { permission: 'invoices.view' },
    palette: {
      keywords: ['contracts', 'recurring', 'maintenance'],
      descriptionKey: 'nav:search.serviceAgreements',
    },
  },
  monthlyBills: {
    to: '/monthly-bills',
    labelKey: 'nav:items.monthlyBills',
    icon: Receipt,
    access: { permission: 'financials.view' },
    palette: {
      keywords: ['expenses', 'recurring costs'],
      descriptionKey: 'nav:search.monthlyBills',
    },
  },
  payroll: {
    to: '/payroll',
    labelKey: 'nav:items.payroll',
    icon: Wallet,
    access: { minRole: 'Admin' },
    palette: { keywords: ['wages', 'pay run'], descriptionKey: 'nav:search.payroll' },
  },
  reports: {
    to: '/reports',
    labelKey: 'nav:items.reports',
    icon: BarChart3,
    access: { permission: 'reports.view' },
    palette: { keywords: ['analytics', 'insights'], descriptionKey: 'nav:search.reports' },
  },
  employees: {
    to: '/employees',
    labelKey: 'nav:items.employees',
    icon: Users,
    access: { permission: 'employees.view' },
    palette: { keywords: ['crew', 'staff', 'team'], descriptionKey: 'nav:search.employees' },
  },
  timesheets: {
    to: '/timesheets',
    labelKey: 'nav:items.timesheets',
    icon: Clock,
    access: { permission: 'time.view' },
    palette: {
      keywords: ['time', 'hours', 'clock in', 'punches'],
      descriptionKey: 'nav:search.timesheets',
    },
  },
  qc: {
    to: '/qc',
    labelKey: 'nav:items.qc',
    icon: ClipboardCheck,
    access: { permission: 'qc.view' },
    palette: {
      keywords: ['quality control', 'checklists', 'inspections'],
      descriptionKey: 'nav:search.qc',
    },
  },
  users: {
    to: '/users',
    labelKey: 'nav:items.users',
    icon: UserCog,
    access: { permission: 'users.view' },
    palette: {
      keywords: ['accounts', 'permissions', 'roles', 'access'],
      descriptionKey: 'nav:search.users',
    },
  },
  clients: {
    to: '/clients',
    labelKey: 'nav:items.clients',
    icon: Contact,
    // Mirrors the api worker: GET /api/v1/customers calls requireManagerOrAdmin,
    // so a crew member who saw this link would reach a 403. The room is no longer
    // a placeholder — it is the real Clients app.
    access: { minRole: 'Manager' },
    palette: {
      keywords: ['customers', 'contacts'],
      descriptionKey: 'nav:search.clients',
    },
  },
  notificationSettings: {
    to: '/settings/notifications',
    labelKey: 'nav:items.notificationSettings',
    icon: Bell,
    // A person's own alert choices (GET/PUT /api/v1/notifications/settings take any signed-in
    // user), not company settings, so crew reach it too.
    access: 'always',
    palette: {
      keywords: ['alerts', 'push', 'email preferences'],
      descriptionKey: 'nav:search.notificationSettings',
    },
  },
  billing: {
    to: '/billing',
    labelKey: 'nav:items.qelmSubscription',
    icon: CreditCard,
    // Stricter than the server on purpose: `GET /api/v1/billing` today authenticates
    // the caller but applies no permission or role guard, so every signed-in user can
    // read the tenant's subscription state. Hiding the link does NOT fix that; the
    // server-side gap is tracked separately.
    access: { permission: 'billing.view' },
    palette: {
      keywords: ['subscription', 'plan', 'upgrade'],
      descriptionKey: 'nav:search.billing',
    },
  },
  activity: {
    to: '/activity',
    labelKey: 'nav:items.activity',
    icon: History,
    access: { permission: 'activity.view' },
    palette: { keywords: ['audit log', 'history'], descriptionKey: 'nav:search.activity' },
  },
  settings: {
    to: '/settings',
    labelKey: 'nav:items.companySettings',
    icon: Settings,
    access: { permission: 'settings.view' },
    palette: {
      keywords: ['company', 'branding', 'preferences'],
      descriptionKey: 'nav:search.settings',
    },
  },
  support: {
    to: '/support',
    labelKey: 'nav:items.qelmSupport',
    icon: LifeBuoy,
    // Support stays open to everyone: needing help is not a privilege.
    access: 'always',
    palette: { keywords: ['help', 'contact', 'ticket'], descriptionKey: 'nav:search.support' },
  },
} satisfies Record<string, NavItem>;

export type NavDestinationId = keyof typeof NAV_DESTINATIONS;

/**
 * Nested left navigation for the SIDEBAR shell: main category first, then real
 * sub-destinations. This avoids hiding whole feature areas behind page-local tabs
 * and keeps Settings/Support explicit.
 */
const SIDEBAR_LAYOUT: Array<{ labelKey: string; icon: LucideIcon; ids: NavDestinationId[] }> = [
  {
    labelKey: 'nav:groups.operations',
    icon: Briefcase,
    ids: ['jobs', 'dispatch', 'servicetitan', 'inventory', 'fleet'],
  },
  {
    labelKey: 'nav:groups.finances',
    icon: Landmark,
    ids: ['estimates', 'invoices', 'serviceAgreements', 'monthlyBills', 'payroll', 'reports'],
  },
  {
    labelKey: 'nav:groups.people',
    icon: Users,
    ids: ['employees', 'timesheets', 'qc', 'users'],
  },
  {
    labelKey: 'nav:groups.company',
    icon: Settings,
    ids: ['notificationSettings', 'billing', 'activity'],
  },
];

export const NAV_GROUPS: NavGroup[] = SIDEBAR_LAYOUT.map(({ labelKey, icon, ids }) => ({
  labelKey,
  icon,
  items: ids.map((id) => NAV_DESTINATIONS[id]),
}));

// The Home KPI dashboard — the FIRST nav destination in the sidebar, rendered
// ungrouped above the grouped sections (an index route: EXACT active match).
export const HOME_ITEM: NavItem = NAV_DESTINATIONS.home;

// The LAUNCHER groups — plain nouns, in this decided order. Every app sits in
// exactly one group; Settings does NOT appear here (it lives behind the profile/
// gear menu in the platform shell). ServiceTitan is filed under Work: it is the
// one destination beyond the decided lists, and the launcher must hold ALL apps
// — its tile only renders for the tenant that has the integration.
const LAUNCHER_LAYOUT: Array<{ labelKey: string; ids: NavDestinationId[] }> = [
  { labelKey: 'nav:launcher.work', ids: ['jobs', 'dispatch', 'servicetitan', 'qc'] },
  { labelKey: 'nav:launcher.money', ids: ['invoices', 'estimates', 'monthlyBills'] },
  {
    labelKey: 'nav:launcher.people',
    ids: ['clients', 'timesheets', 'payroll', 'employees', 'users'],
  },
  { labelKey: 'nav:launcher.assets', ids: ['inventory', 'fleet', 'serviceAgreements'] },
  { labelKey: 'nav:launcher.insight', ids: ['reports', 'activity'] },
];

export const LAUNCHER_GROUPS: LauncherGroup[] = LAUNCHER_LAYOUT.map(({ labelKey, ids }) => ({
  labelKey,
  items: ids.map((id) => NAV_DESTINATIONS[id]),
}));

// The BAR TABS (draft — one line to change later): the daily destinations that
// stay visible in the platform top bar itself, launcher or no launcher.
const BAR_TAB_IDS: NavDestinationId[] = ['jobs', 'dispatch', 'invoices', 'clients'];

export const BAR_TABS: NavItem[] = BAR_TAB_IDS.map((id) => NAV_DESTINATIONS[id]);

// Chrome destinations that are NOT launcher apps: Home (the index), and the
// settings/support cluster the platform shell keeps behind its profile/gear menu.
// The cmd-K palette composes these with the launcher groups so every destination
// the user may reach is reachable from the keyboard.
const CHROME_IDS: NavDestinationId[] = [
  'home',
  'settings',
  'notificationSettings',
  'billing',
  'support',
];

export const CHROME_DESTINATIONS: NavItem[] = CHROME_IDS.map((id) => NAV_DESTINATIONS[id]);

/** A palette entry: the nav destination it jumps to, plus the key that names it. */
export interface PaletteDestination extends NavItem {
  id: NavDestinationId;
}

/**
 * The cmd-K palette's entries, DERIVED — every destination in `NAV_DESTINATIONS`, in declaration
 * order, carrying its own `access` rule with it. There is no filter and no opt-in: `palette` is
 * required above, so the palette is the nav list.
 *
 * Before this the sidebar shell hand-wrote ten palette entries against these destinations and
 * re-typed each one's access rule beside it. Nothing checked the two agreed, so the palette was a
 * back door around the hidden sidebar waiting to happen: add a destination, tighten a rule, or
 * rename a route, and only one of the two lists moved. `AppShell.test.tsx` pins the relationship
 * from both ends — every derived entry appears when its rule passes and is gone when it fails,
 * and no palette entry exists without a nav destination behind it.
 */
export const PALETTE_DESTINATIONS: PaletteDestination[] = (
  Object.entries(NAV_DESTINATIONS) as Array<[NavDestinationId, NavItem]>
).map(([id, destination]) => ({ id, ...destination }));

/**
 * A create action in the top bar's New menu. It is not a NavItem: a nav item names a
 * PLACE and mirrors the guard on that place's LIST endpoint, while this names an ACT
 * and must mirror the guard on the POST that performs it. The two disagree often
 * enough that sharing one shape would invite the wrong rule to be copied across.
 */
export interface NewAction {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  access: AccessRule;
  /** Single letter shown in the menu; pressing it while the menu is open opens the form. */
  key: string;
}

/**
 * ⛔ THE `*.create` PERMISSION KEYS DO NOT ALL MATCH THE ROUTES. Every rule below was read
 * off the api worker's `routes/api.ts`, not inferred from the key name:
 *
 *   POST /jobs        jobs.create
 *   POST /estimates   requireManagerOrAdmin
 *   POST /customers   requireManagerOrAdmin
 *   POST /invoices    invoices.create
 *
 * Gate each item by the rule its OWN endpoint applies. A menu built from the key that
 * shares the item's name would show a crew member two forms that answer 403.
 *
 * Four items, and deliberately no fifth group. The approved design carries a
 * "For this workspace" group of trade-shaped actions beneath these — a service
 * agreement for a maintenance trade, an expense for a supply-heavy one. NOTHING
 * FEEDS IT YET: `tenant_features` stores booleans, and the session carries two of
 * them. The caption would name a difference that does not exist, so the group waits
 * for a real signal (PostHog tenant cohorts are the intended source) rather than
 * shipping as decoration.
 */
export const NEW_ACTIONS: NewAction[] = [
  {
    to: '/jobs/new',
    labelKey: 'nav:new.job',
    icon: Briefcase,
    access: { permission: 'jobs.create' },
    key: 'j',
  },
  {
    to: '/estimates/new',
    labelKey: 'nav:new.estimate',
    icon: ClipboardList,
    access: { minRole: 'Manager' },
    key: 'e',
  },
  {
    to: '/invoices/new',
    labelKey: 'nav:new.invoice',
    icon: FileText,
    access: { permission: 'invoices.create' },
    key: 'i',
  },
  {
    to: '/clients/new',
    labelKey: 'nav:new.client',
    icon: Contact,
    access: { minRole: 'Manager' },
    key: 'c',
  },
];
